'use memo';

import { useState } from 'react';
import {
  AgxBodyText,
  AgxButton,
  AgxColumn,
  AgxHeader,
  AgxSparkleForm,
  Breakpoints,
  CampaignDetailModel,
  FormConfigType,
  SPECIAL_CHARACTERS,
  StakeholderType,
  useWindowSize,
} from '@urbanx/agx-ui-components';
import dayjs from 'dayjs';
import { setAndShowErrorToast } from 'store/config';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useFormLoader } from 'hooks/useFormLoader';
import { selectContract } from 'features/campaigns/campaignsReducer';
import { Tabs } from 'types/Tabs';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { formatListOfStrings } from 'utils/formatString';
import { getAllBuyerNames, prepareSignatories } from 'utils/vendorBuyerUtil';
import FormDetailsHeader from '../../Common/FormDetailsHeader';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { ContractDetailModel } from '@urbanx/agx.campaigns.client';

interface ResumeContractSubmissionProps {
  campaign: CampaignDetailModel;
  contract: ContractDetailModel;
  currentTab: Tabs;
  lastUpdate: Date | undefined;
  mobile?: boolean;
}

const ResumeContractSubmission = ({
  campaign,
  contract,
  currentTab,
  lastUpdate,
  mobile = false,
}: ResumeContractSubmissionProps) => {
  const { address, campaignId } = campaign;

  const lastUpdatedOn = lastUpdate
    ? `Last updated ${dayjs(lastUpdate).format('DD/MM/YY')}`
    : 'Not yet updated';
  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;
  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const dispatch = useAppDispatch();
  const { loadForm } = useFormLoader();
  const [disableButtons, setDisableButtons] = useState(false);

  const resumeContractSubmission = async () => {
    try {
      setDisableButtons(true);

      await loadForm(campaignId, contract.contractId ?? '', FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to resume Contract submission'));
    }

    setDisableButtons(false);
  };
  const allBuyers = prepareSignatories(contract.buyers, StakeholderType.Buyer);
  const allBuyerNames = getAllBuyerNames(allBuyers);

  return (
    <>
      <FormDetailsHeader
        id={'resume-contract-submission-header'}
        formName="Contract"
        currentTab={currentTab}
        detail={lastUpdatedOn}
        onBack={() => dispatch(selectContract(null))}
      />
      <AgxColumn centered veryLargeGap extraClasses="resumeFormDetails">
        <AgxHeader size={2} centered>
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxHeader>
        <AgxBodyText medium centered>
          {formatListOfStrings(allBuyerNames)} {SPECIAL_CHARACTERS.BULLET}{' '}
          {formatCurrencyWithConditionalDecimals(contract.salePrice ?? 0, 2)}
        </AgxBodyText>
        <AgxColumn smallGap centered>
          <AgxBodyText medium centered>
            There are more fields to fill,
          </AgxBodyText>
          <AgxBodyText medium centered>
            let's continue from where you left off
          </AgxBodyText>
        </AgxColumn>
        <AgxSparkleForm
          width={isMobile ? 150 : 200}
          height={isMobile ? 150 : 200}
        />
      </AgxColumn>
      <div className="campaignModalButtons">
        <AgxButton
          id="btn-resumeSubmission"
          hollow={!mobile}
          primary={mobile}
          wide={mobile}
          large
          text="Resume Submission"
          onClick={resumeContractSubmission}
          shrinkOnLargerDevices
          disabled={disableButtons}
        />
      </div>
    </>
  );
};

export default ResumeContractSubmission;
